/**
 * Adiciona itens via Cart API e redireciona ao checkout Shopify.
 * Trata erros JSON (422) que às vezes não falham em res.ok.
 */

import { submitCartToCheckout } from './vestfirma-cart-submit-checkout.js';
import {
  getCartSectionIds,
  notifyCartAddAndOpen,
  openVestfirmaCartDrawer,
} from './vestfirma-cart-after-add.js';

export { submitCartToCheckout, openVestfirmaCartDrawer, notifyCartAddAndOpen, getCartSectionIds };

/**
 * @param {unknown} data
 */
function cartAddFailed(data) {
  if (!data || typeof data !== 'object') return false;
  const record = /** @type {Record<string, unknown>} */ (data);
  if (record.status === 'error') return true;
  const status = Number(record.status);
  if (Number.isFinite(status) && status >= 400) return true;
  if (typeof record.description === 'string' && record.description.length > 0) return true;
  return false;
}

/**
 * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} items
 * @param {{ errorMessage?: string; sections?: string; source?: string; sourceId?: string; productId?: string; itemCount?: number }} [options]
 */
export async function addItemsToCart(items, options = {}) {
  if (!items?.length) {
    throw new Error(options.errorMessage || 'Nenhum item para adicionar.');
  }

  const sections = options.sections || getCartSectionIds();
  const body = { items };
  if (sections) body.sections = sections;

  const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok || cartAddFailed(data)) {
    const record = /** @type {Record<string, string>} */ (data);
    const msg =
      record.description ||
      record.message ||
      options.errorMessage ||
      'Não foi possível adicionar ao carrinho. Tente de novo.';
    throw new Error(msg);
  }

  const cart = data;

  const itemCount =
    options.itemCount ??
    items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  notifyCartAddAndOpen(cart, options.sourceId || 'vestfirma-pdp', {
    source: options.source || 'vestfirma-pdp',
    itemCount,
    productId: options.productId,
    sections: data.sections,
  });

  return cart;
}

/**
 * @param {Array<{ id: number; quantity: number; properties?: Record<string, string> }>} items
 * @param {{ checkoutUrl?: string; errorMessage?: string; sections?: string }} [options]
 */
export async function addItemsAndGoCheckout(items, options = {}) {
  if (!items?.length) {
    throw new Error(options.errorMessage || 'Nenhum item para comprar.');
  }

  const body = { items };
  if (options.sections) body.sections = options.sections;
  else {
    const sections = getCartSectionIds();
    if (sections) body.sections = sections;
  }

  const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok || cartAddFailed(data)) {
    const record = /** @type {Record<string, string>} */ (data);
    const msg =
      record.description ||
      record.message ||
      options.errorMessage ||
      'Não foi possível adicionar ao carrinho. Tente de novo.';
    throw new Error(msg);
  }

  if (options.checkoutUrl) {
    window.location.assign(options.checkoutUrl);
    return data;
  }

  submitCartToCheckout();
  return data;
}

if (typeof window !== 'undefined') {
  window.VestfirmaCheckoutGo = {
    addItemsToCart,
    addItemsAndGoCheckout,
    cartAddFailed,
    submitCartToCheckout,
  };
}
